import type { Env } from './presets';

export interface Instrument {
  noteOn(note: number, velocity: number, time: number): void;
  noteOff(note: number, time: number): void;
  allNotesOff(time: number): void;
  /** Sustain pedal (MIDI CC64). */
  setSustain(on: boolean, time: number): void;
  /** Cents applied to every sounding voice (pitch wheel). */
  setBend(cents: number, time: number): void;
  /** 0..1 modulation wheel. */
  setMod(amount: number, time: number): void;
  /** Fade out quickly and release every node. */
  dispose(): void;
}

export const midiToFreq = (note: number): number => 440 * Math.pow(2, (note - 69) / 12);

/** Perceptual velocity curve: 0..127 to 0..1 gain. */
export const velocityGain = (velocity: number): number => Math.pow(Math.max(1, velocity) / 127, 1.4);

/** Start an ADSR envelope on `param`, peaking at `peak`. */
export function envAttack(param: AudioParam, env: Env, peak: number, t: number, floor = 0): void {
  param.cancelScheduledValues(t);
  param.setValueAtTime(floor, t);
  param.linearRampToValueAtTime(peak, t + Math.max(0.001, env.a));
  param.setTargetAtTime(floor + (peak - floor) * env.s, t + Math.max(0.001, env.a), Math.max(0.005, env.d / 3));
}

/** Release from wherever the envelope currently is. */
export function envRelease(param: AudioParam, release: number, t: number, floor = 0): void {
  holdAt(param, t);
  param.setTargetAtTime(floor, t, Math.max(0.004, release / 4));
}

/** Freeze an automated parameter at its current value at time `t`. */
export function holdAt(param: AudioParam, t: number): void {
  if (typeof param.cancelAndHoldAtTime === 'function') {
    param.cancelAndHoldAtTime(t);
  } else {
    const v = param.value;
    param.cancelScheduledValues(t);
    param.setValueAtTime(v, t);
  }
}

/**
 * Shared modulation sources for an instrument: a ConstantSource for pitch
 * bend (in cents) that every oscillator's `detune` listens to, and an LFO
 * whose depth the mod wheel can deepen.
 */
export class ModSources {
  readonly bend: ConstantSourceNode;
  readonly lfo: OscillatorNode;
  readonly lfoPitch: GainNode;
  readonly lfoFilter: GainNode;
  private basePitch: number;

  constructor(
    private ctx: AudioContext,
    lfo: { rate: number; pitch: number; filter: number } = { rate: 5.5, pitch: 0, filter: 0 },
  ) {
    this.bend = ctx.createConstantSource();
    this.bend.offset.value = 0;
    this.bend.start();
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = lfo.rate;
    this.lfoPitch = ctx.createGain();
    this.lfoFilter = ctx.createGain();
    this.basePitch = lfo.pitch;
    this.lfoPitch.gain.value = lfo.pitch;
    this.lfoFilter.gain.value = lfo.filter;
    this.lfo.connect(this.lfoPitch);
    this.lfo.connect(this.lfoFilter);
    this.lfo.start();
  }

  connectPitch(param: AudioParam): void {
    this.bend.connect(param);
    this.lfoPitch.connect(param);
  }

  /** Undo connectPitch when a voice ends, so finished voices can be freed. */
  disconnectPitch(param: AudioParam): void {
    for (const src of [this.bend, this.lfoPitch]) {
      try {
        src.disconnect(param);
      } catch {
        /* not connected */
      }
    }
  }

  disconnectFilter(param: AudioParam): void {
    try {
      this.lfoFilter.disconnect(param);
    } catch {
      /* not connected */
    }
  }

  setBend(cents: number, t: number): void {
    this.bend.offset.setTargetAtTime(cents, t, 0.008);
  }

  setMod(amount: number, t: number): void {
    // The mod wheel adds up to 40 cents of vibrato on top of the preset's own.
    this.lfoPitch.gain.setTargetAtTime(this.basePitch + amount * 40, t, 0.03);
  }

  dispose(t: number): void {
    this.bend.stop(t);
    this.lfo.stop(t);
    const ctx = this.ctx;
    setTimeout(() => {
      this.lfoPitch.disconnect();
      this.lfoFilter.disconnect();
    }, Math.max(0, (t - ctx.currentTime) * 1000) + 100);
  }
}
